import { VideoPlayer } from '@/components/video-player';
import CommonModal from '@/components/customModal/commonModal/commonModal';
import { Box, Button } from '@mui/material';
import React from 'react';

/** Mux / HLS source built for `VideoPlayer` (e.g. from `useMuxSrcConstructor`). */
export type VideoDialogMuxSource = {
  type: string;
  src: string;
  poster?: string | null;
};

/**
 * Live stream: pre-built Mux source. Media library: object with at least `id`
 * for `/api/media/:id/videourl` (may be a full media file row).
 */
export type VideoDialogPayload =
  | {
      type: 'live_stream';
      source: VideoDialogMuxSource;
      file?: unknown;
    }
  | (Record<string, unknown> & { id: number | string });

export interface VideoDialogProps {
  open: boolean;
  onClose: () => void;
  /** When `open` is true, should be set so the player can render. */
  data: VideoDialogPayload | null;
}

function isLiveStreamPayload(
  data: VideoDialogPayload
): data is Extract<VideoDialogPayload, { type: 'live_stream' }> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type?: string }).type === 'live_stream'
  );
}

function getVideoSource(
  data: VideoDialogPayload | null
): VideoDialogMuxSource | undefined {
  if (!data) return undefined;
  if (isLiveStreamPayload(data)) {
    return data.source;
  }
  if ('id' in data && data.id != null) {
    return {
      src: `/api/media/${data.id}/videourl`,
      type: 'application/x-mpegURL',
      poster: `/api/media/${data.id}/videothumbnail`,
    };
  }
  return undefined;
}

const VIDEO_STAGE_SX = {
  width: '100%',
  maxWidth: '100%',
  /** 16:9 playback area; height follows width until maxHeight caps it */
  aspectRatio: '16 / 9',
  maxHeight: { xs: '48vh', sm: '58vh', md: '68vh' },
  minHeight: 180,
  mx: 'auto',
  bgcolor: '#0a0a0a',
  borderRadius: 1,
  overflow: 'hidden',
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  /** Let Video.js / video element fill the stage */
  '& .video-js': {
    width: '100% !important',
    height: '100% !important',
    paddingTop: 0,
  },
};

const VideoDialog: React.FC<VideoDialogProps> = ({ open, onClose, data }) => {
  const source = getVideoSource(data);

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Video"
      maxWidth="lg"
      fullWidth
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={VIDEO_STAGE_SX}>
          {source ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                '& [data-vjs-player]': {
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >
              <VideoPlayer source={source} />
            </Box>
          ) : null}
        </Box>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
            autoFocus
          >
            Close
          </Button>
        </Box>
      </Box>
    </CommonModal>
  );
};

export default VideoDialog;
